import React, { useState, useEffect, useCallback } from "react";
import { Save, Copy, Zap, ShieldCheck, Globe, Trash2 } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const FreshchatConfig = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [freshchatConfig, setFreshchatConfig] = useState({
    api_token: "",
    freshchat_url: "",
    is_active: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/freshchat/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setFreshchatConfig({
          api_token: response.config.api_token ? UNCHANGED_KEY : "",
          freshchat_url: response.config.freshchat_url || "",
          is_active: response.config.is_active || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.error("Failed to load config:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!freshchatConfig.api_token.trim()) {
      showToast("Please enter your API token", "error");
      return;
    }

    if (!freshchatConfig.freshchat_url.trim()) {
      showToast("Please enter your Freshchat URL", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/freshchat/config", { aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify(freshchatConfig),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Freshchat configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/freshchat", {
        method: "DELETE"
      });
      if (response.success) {
        setFreshchatConfig({
          api_token: "",
          freshchat_url: "",
          is_active: false,
        });
        setWebhookUrl("");
        showToast("Freshchat integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith('http://') || webhookUrl.startsWith('https://')) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith('/') ? path : '/' + path;
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api')) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!freshchatConfig.is_active) {
      if (!freshchatConfig.api_token?.trim() || !freshchatConfig.freshchat_url?.trim()) {
        showToast("Please enter and save your API token and URL first", "error");
        return;
      }
      if (freshchatConfig.api_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !freshchatConfig.is_active;
      const response = await ApiClient.request("/freshchat/config", { aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setFreshchatConfig((prev) => ({ ...prev, is_active: response.active }));
        showToast(response.message, "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader 
          title="Freshchat Sync"
          description="Bridge your agentic intelligence with Freshchat's omni-channel ecosystem."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${freshchatConfig.is_active ? "text-[#83bbe1]" : "text-gray-300"}`}>
                {freshchatConfig.is_active ? "Operational" : "Offline"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={freshchatConfig.is_active}
                  onChange={toggleActive}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
              </label>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Main Configuration Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Freshchat API Access</h3>
                <p className="text-[11px] font-medium text-gray-400">Securely connect your Freshchat account</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Freshchat URL */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Freshchat Data Center URL
                </label>
                <div className="relative group">
                  <input
                    type="url"
                    value={freshchatConfig.freshchat_url}
                    onChange={(e) =>
                      setFreshchatConfig({
                        ...freshchatConfig,
                        freshchat_url: e.target.value,
                      })
                    }
                    placeholder="https://ms-your-org.freshchat.com"
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                    <Globe size={16} className="text-gray-400" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  Example: <code>https://api.freshchat.com</code> or your specific DC URL.
                </p>
              </div>

              {/* API Token */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Admin API Token
                </label>
                <div className="relative group">
                  <input
                    type="password"
                    value={freshchatConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : freshchatConfig.api_token}
                    onChange={(e) =>
                      setFreshchatConfig({
                        ...freshchatConfig,
                        api_token: e.target.value,
                      })
                    }
                    onFocus={(e) => {
                      if (freshchatConfig.api_token === UNCHANGED_KEY) {
                        setFreshchatConfig({ ...freshchatConfig, api_token: "" });
                      }
                    }}
                    placeholder={freshchatConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : "Your Freshchat API token"}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                    <Zap size={16} className="text-gray-400" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  Found in <span className="font-bold">Settings → API Tokens</span> in your Freshchat dashboard.
                </p>
              </div>

              <div className="pt-4 border-t border-gray-50 flex justify-between items-center">
                {freshchatConfig.freshchat_url && (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 border border-red-200 text-red-600 rounded-2xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm"
                  >
                    <Trash2 size={18} />
                    Delete Integration
                  </button>
                )}
                <button
                  onClick={saveConfig}
                  disabled={saving || !freshchatConfig.api_token?.trim() || !freshchatConfig.freshchat_url?.trim()}
                  className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm ml-auto"
                >
                  <Save size={18} />
                  {saving ? "Saving..." : "Save Freshchat Config"}
                </button>
              </div>
            </div>
          </div>

          {/* Webhook Card */}
          {webhookUrl && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideUp">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Globe size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Webhook Delivery</h3>
                  <p className="text-[11px] font-medium text-gray-400">Receive real-time updates from Freshchat</p>
                </div>
              </div>

              <div className="p-5 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 group transition-all hover:border-blue-200 hover:bg-blue-50/10">
                <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Live Webhook URL
                </label>
                <div className="flex items-center justify-between gap-4">
                  <div className="font-mono text-xs text-gray-600 truncate flex-1 leading-loose">
                    {displayUrl}
                  </div>
                  <button
                    onClick={copyWebhookUrl}
                    className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-900 hover:text-white rounded-xl text-[10px] font-bold text-gray-500 transition-all border border-gray-100 shadow-sm"
                  >
                    <Copy size={12} />
                    {copied ? "Copied!" : "Copy URL"}
                  </button>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-400">
                Configuration for Freshchat webhooks can be found in your account settings under 'Webhooks'.
              </p>
            </div>
          )}
        </div>

      </div>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Delete Freshchat Integration"
        message="Are you sure you want to delete this integration config? This will remove all credentials and disable the webhook."
        confirmText="Delete Integration"
        isLoading={saving}
      />
    </div>
  );
};

export default FreshchatConfig;
